import { Component } from '@angular/core'
import { SeFeDocsExampleComponent } from 'se-fe-docs-example'
import { CentralServiceResources as cs } from 'se-resource-types'

const exampleHtml = `
<se-po-persona-picker
  (pick)="onPick($event)"
  (edit)="onEdit($event)"
  (action)="onAction($event)"
  [profiles]="profiles"
  [profilesFrames]="profilesFrames"
  [requiredFields]="requiredFields"
></se-po-persona-picker>
`

const exampleTypescript = `
import { Component } from '@angular/core'
import { CentralServiceResources as cs } from 'se-resource-types'

export class PersonaPickerExampleComponent {
  // These are FE-provided per-survey required fields and may vary.
  // When provided, Persona Picker uses exactly these keys (no auto-defaulting).
  public requiredFields = {
    adult: ['first_name', 'last_name', 'date_of_birth', 'gender', 'phone', 'email_address_1'],
    minor: ['first_name', 'last_name', 'date_of_birth', 'gender', 'graduation_year', 'street_address_1'],
  }
  public profiles: cs.Households.UserProfile[] = [
    {
      relationship: 'other',
      access: 'manager',
      persona: {
        id: 1,
        first_name: 'Robert',
        full_name: 'Robert Bobson',
        last_name: 'Bobson',
        date_of_birth: '1980-01-05',
        gender: 'male',
        age: 25,
      }
    } as any as cs.Households.UserProfile,
    {
      relationship: 'self',
      access: 'owner',
      persona: {
        id: 2,
        first_name: 'Bob',
        full_name: 'Bob Roberts',
        last_name: 'Roberts',
        date_of_birth: '1950-05-01',
        gender: 'male',
        age: 40,
        phone_numbers: [{ number: '555-0000' }],
      }
    } as any as cs.Households.UserProfile,
    {
      relationship: 'other',
      access: 'owner',
      persona: {
        id: 3,
        first_name: 'Bobbie Sue',
        full_name: 'Bobbie Sue McGee',
        last_name: 'Mcgee',
        date_of_birth: '1999-11-15',
        gender: 'female',
        age: 15,
        profile_images: [
          {
            image_type: 'original',
            url: 'https://cdn1.sportngin.com/attachments/profiles/0274-138659013/PacmanJunior.jpg'
          },
          {
            image_type: 'crop',
            url: 'https://cdn1.sportngin.com/attachments/profiles/0274-138659013/PacmanJunior_crop.jpg'
          },
          {
            image_type: 'crop_icon',
            url: 'https://cdn1.sportngin.com/attachments/profiles/0274-138659013/PacmanJunior_crop_icon.jpg'
          }
        ]
      }
    } as any as cs.Households.UserProfile,
    {
      relationship: 'other',
      access: 'owner',
      persona: {
        id: 4,
        first_name: 'Sue',
        full_name: 'Sue McGee',
        last_name: 'Mcgee',
        date_of_birth: '2000-01-15',
        gender: 'female',
        age: 16,
        graduation_year: 2027,
        address: {
          country: 'US',
          street_address_1: '123 Main St',
          city: 'Springfield',
          state_or_province: 'MN',
          zip: '55111',
        }
      }
    } as any as cs.Households.UserProfile,
    {
      relationship: 'other',
      access: 'owner',
      persona: {
        id: 5,
        first_name: 'Luke',
        full_name: 'Luke Mahilos',
        last_name: 'Mahilos',
        date_of_birth: '2000-01-15',
        gender: 'female',
        age: 30,
        phone_numbers: [{ number: '555-1111' }],
        email_addresses: [{ address: 'luke.mahilos@example.com' }],
      },
    } as any as cs.Households.UserProfile,
  ]

  public profilesFrames: cs.Households.UserProfileFrame[] = [
    {
      persona_id: 1,
      header: {
        type: 'caution',
        text: 'Pending Registration'
      },
      disable: false,
      actions: [{
        name: 'contine',
        label: 'Continue'
      }]
    } as cs.Households.UserProfileFrame,
    {
      persona_id: 3,
      header: {
        type: 'critical',
        text: 'Require Membership'
      },
      actions: [
        {
          name: 'contine',
          label: 'Continue'
        },
        {
          name: 'cancel',
          label: 'Cancel'
        }
      ],
      disable: false
    } as cs.Households.UserProfileFrame,
    {
      persona_id: 5,
      header: {
        type: 'caution',
        text: 'Pending Registration'
      },
      disable: true,
      actions: [{
        name: 'contine',
        label: 'Continue'
      }]
    } as cs.Households.UserProfileFrame,
  ]

  public onPick(e: any): void {
    console.log('onPick', e)
  }

  public onEdit(e: any): void {
    console.log('onEdit', e)
  }

  public onAction(e: any): void {
    console.log('onAction', e)
  }
}

`

const template = `<se-fe-docs-example [example]="this">${exampleHtml}</se-fe-docs-example>`

@Component({
  selector: 'se-po-persona-picker-example',
  template,
  styleUrls: ['./persona-picker-docs.component.scss'],
  standalone: false,
})
export class PersonaPickerExampleComponent extends SeFeDocsExampleComponent {
  public files = [
    { src: exampleHtml, name: 'Template', lang: 'html' },
    { src: exampleTypescript, name: 'Typescript', lang: 'typescript' },
  ]
  public requiredFields: { adult: string[]; minor: string[] } = {
    adult: ['first_name', 'last_name', 'date_of_birth', 'gender', 'phone', 'email_address_1'],
    minor: ['first_name', 'last_name', 'date_of_birth', 'gender', 'graduation_year', 'street_address_1'],
  }
  public profiles: cs.Households.UserProfile[] = [
    {
      relationship: 'other',
      access: 'manager',
      persona: {
        id: 1,
        first_name: 'Robert',
        full_name: 'Robert Bobson',
        last_name: 'Bobson',
        date_of_birth: '1980-01-05',
        gender: 'male',
        age: 25,
      }
    } as any as cs.Households.UserProfile,
    {
      relationship: 'self',
      access: 'owner',
      persona: {
        id: 2,
        first_name: 'Bob',
        full_name: 'Bob Roberts',
        last_name: 'Roberts',
        date_of_birth: '1950-05-01',
        gender: 'male',
        age: 40,
        phone_numbers: [{ number: '555-0000' }],
      }
    } as any as cs.Households.UserProfile,
    {
      relationship: 'other',
      access: 'owner',
      persona: {
        id: 3,
        first_name: 'Bobbie Sue',
        full_name: 'Bobbie Sue McGee',
        last_name: 'Mcgee',
        date_of_birth: '1999-11-15',
        gender: 'female',
        age: 15,
        profile_images: [
          {
            image_type: 'original',
            url: 'https://cdn1.sportngin.com/attachments/profiles/0274-138659013/PacmanJunior.jpg'
          },
          {
            image_type: 'crop',
            url: 'https://cdn1.sportngin.com/attachments/profiles/0274-138659013/PacmanJunior_crop.jpg'
          },
          {
            image_type: 'crop_icon',
            url: 'https://cdn1.sportngin.com/attachments/profiles/0274-138659013/PacmanJunior_crop_icon.jpg'
          }
        ]
      }
    } as any as cs.Households.UserProfile,
     {
      relationship: 'other',
      access: 'owner',
      persona: {
        id: 4,
        first_name: 'Sue',
        full_name: 'Sue McGee',
        last_name: 'Mcgee',
        date_of_birth: '2000-01-15',
        gender: 'female',
        age: 16,
        graduation_year: 2027,
        address: {
          country: 'US',
          street_address_1: '123 Main St',
          city: 'Springfield',
          state_or_province: 'MN',
          zip: '55111',
        }
      }
    } as any as cs.Households.UserProfile,
    {
      relationship: 'other',
      access: 'owner',
      persona: {
        id: 5,
        first_name: 'Luke',
        full_name: 'Luke Mahilos',
        last_name: 'Mahilos',
        date_of_birth: '2000-01-15',
        gender: 'female',
        age: 30,
        phone_numbers: [{ number: '555-1111' }],
        email_addresses: [{ address: 'luke.mahilos@example.com' }],
      },
    } as any as cs.Households.UserProfile,
  ]

  public profilesFrames: cs.Households.UserProfileFrame[] = [
    {
      persona_id: 1,
      header: {
        type: 'caution',
        text: 'Pending Registration'
      },
      disable: false,
      actions: [{
        name: 'contine',
        label: 'Continue'
      }]
    } as cs.Households.UserProfileFrame,
    {
      persona_id: 3,
      header: {
        type: 'critical',
        text: 'Require Membership'
      },
      actions: [
        {
          name: 'contine',
          label: 'Continue'
        },
        {
          name: 'cancel',
          label: 'Cancel'
        }
      ],
      disable: false
    } as cs.Households.UserProfileFrame,
    {
      persona_id: 5,
      header: {
        type: 'caution',
        text: 'Pending Registration'
      },
      disable: true,
      actions: [{
        name: 'contine',
        label: 'Continue'
      }]
    } as cs.Households.UserProfileFrame,
  ]

  public onPick(e: any): void {
    console.log('onPick', e)
  }

  public onEdit(e: any): void {
    console.log('onEdit', e)
  }

  public onAction(e: any): void {
    console.log('onAction', e)
  }
}
